import { useCallback } from 'react';
import { UNDO_OFFER_MS, type UndoRef } from '@/domain';
import { useUndoWork } from '@/application/usecases/useKits';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { toast, type ToastTone } from '@/ui/primitives/toastStore';

/**
 * The one way an action offers Undo. Section 6 of the request of 5 October 2026:
 * Complete and Send, Mark done, a flag, taking a case, an assignment, a rework
 * and a move between departments all say what happened in a toast that carries
 * Undo for about ten seconds.
 *
 * The caller says what happened and hands over what the server answered with.
 * When the answer carries an undo, the toast offers it; when it does not (the
 * command was queued offline, or the server is a build without undo), the toast
 * is the plain one it always was. Pressing Undo asks the server to put the case
 * back, and the server decides: it may refuse because somebody has acted on the
 * case since, and its reason is shown as it arrives.
 */
export function useOfferUndo() {
  const undo = useUndoWork();
  const { mutateAsync } = undo;

  return useCallback(
    (message: string, ref: UndoRef | null | undefined, tone: ToastTone = 'success') => {
      if (ref === null || ref === undefined) {
        toast(message, { tone });
        return;
      }

      toast(message, {
        tone,
        durationMs: UNDO_OFFER_MS,
        action: {
          label: strings.undo.action,
          onClick: () => {
            void mutateAsync(ref.id)
              .then((result) => {
                const first = result.items[0];
                toast(
                  result.items.length === 1 && first !== undefined
                    ? strings.undo.done(first.jobOrderNo)
                    : strings.undo.doneMany(result.items.length),
                  { tone: 'success' },
                );
                // A selection may be put back in part: say why the rest was not.
                const refusal = result.refused[0];
                if (refusal !== undefined) toast(refusal.detail, { tone: 'error' });
              })
              .catch((error: Error) => toast(failureWords(error), { tone: 'error' }));
          },
        },
      });
    },
    [mutateAsync],
  );
}
